import TextButton from 'LuxKit/Button/TextButton'
import SolidPlusIcon from 'LuxKit/Icons/solid/SolidPlusIcon'
import TextLink from 'LuxKit/TextLink'
import Caption from 'LuxKit/Typography/Caption'
import { MAX_ROOM_LIMIT } from 'constants/offer'
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { getPlural } from 'lib/string/pluralize'
import { getSupportContact } from 'selectors/supportContactSelector'

interface Props {
  className?: string
  contactNumber?: App.GeoContact
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
  roomsCount: number
  saleUnit?: string
  maxRooms?: number
  noServiceContact?: boolean
}

function AddRoomButton(props: Props) {
  const {
    className,
    contactNumber,
    onClick,
    roomsCount,
    saleUnit,
    maxRooms = MAX_ROOM_LIMIT,
    noServiceContact,
  } = props
  const intl = useIntl()
  const unit = saleUnit || 'room'
  const pluralizeSaleUnit = saleUnit
    ? intl.formatMessage({
        id: '/E4sMYR2Ly',
        defaultMessage: '{count} {unitPlural}',
        description: 'Custom sale unit count for max booking limit message when a non-room sale unit is provided',
      }, { count: maxRooms, unitPlural: getPlural(saleUnit, maxRooms) })
    : intl.formatMessage({
        id: 'nA5rj3NHW7',
        defaultMessage: '{count, plural, one {# room} other {# rooms}}',
        description: 'Pluralized sale unit count for max rooms booking limit message',
      }, { count: maxRooms })

  return (
    <div className={className} data-testid="add-room-button">
      {roomsCount < maxRooms && <TextButton
        kind="tertiary"
        size="medium"
        startIcon={<SolidPlusIcon />}
        horizontalOutdent="start"
        onClick={onClick}
      >
        {intl.formatMessage(
          {
            id: 'HY50p/JSMz',
            defaultMessage: 'Add another {unit}',
            description: 'Button text to add another room to occupancy selection',
          },
          { unit },
        )}
      </TextButton>}
      {(roomsCount >= maxRooms && !noServiceContact && contactNumber) &&
        <Caption variant="medium" weight="bold">
          <FormattedMessage
            id="GW6yib32zB"
            defaultMessage="For bookings of more than {pluralizeSaleUnit} please contact our customer service team on <link>{contactNumber}</link>."
            description="Caption directing users to contact support for bookings exceeding the room limit"
            values={{
              pluralizeSaleUnit,
              contactNumber: contactNumber.humanReadable,
              link: (chunks) => <TextLink href={`tel:${contactNumber.number}`} rel="nofollow">{chunks}</TextLink>,
            }}
          />
        </Caption>}
    </div>
  )
}

function mapStateToProps(state: App.State) {
  return {
    contactNumber: getSupportContact(state).defaultContact,
  }
}

export default connect(mapStateToProps)(AddRoomButton)
